import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { formatAmount, money } from '../../lib/money';
import { useNavigate } from 'react-router-dom';
import {
  Button,
  DataTable,
  EmptyState,
  FilterBar,
  KpiTile,
  PageContainer,
  PageHeader,
  Pagination,
  Spinner,
} from '../../ui/components';
import { Icon } from '../../ui/icons';
import { useProperty } from '../../app/PropertyContext';
import { useT } from '../../i18n';
import { itemPath } from '../../lib/slug';
import { buildFilterParams } from '../../lib/filters';
import { catalogApi } from '../catalog/catalogApi';
import { inventoryApi } from './inventoryApi';
import '../requisitions/requisitions.css';

const PAGE_SIZE = 50;
export default function StockOnHandPage() {
  const navigate = useNavigate();
  const t = useT();
  const { activePropertyId } = useProperty();

  const [data, setData] = useState({ items: [], totalCount: 0, page: 1, pageSize: PAGE_SIZE });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchText, setSearchText] = useState('');
  const [search, setSearch] = useState('');
  const [values, setValues] = useState({});
  const [locations, setLocations] = useState([]);
  const [units, setUnits] = useState([]);
  const [page, setPage] = useState(1);
  const debounceRef = useRef(null);

  useEffect(() => {
    let active = true;
    catalogApi.listLocations(activePropertyId).then((res) => { if (active) setLocations(res || []); }).catch(() => {});
    catalogApi.listUnits().then((res) => { if (active) setUnits(res || []); }).catch(() => {});
    return () => { active = false; };
  }, [activePropertyId]);

  const fields = useMemo(() => [
    { id: 'locationIds', label: t('Location'), type: 'multiselect', searchable: true, options: locations.map((l) => ({ value: l.id, label: l.name })) },
    { id: 'includeZero', label: t('Zero stock'), type: 'select', options: [{ value: '', label: t('Include') }, { value: 'false', label: t('Hide zero balances') }], param: 'includeZero' },
  ], [locations, t]);

  const filterParams = useMemo(() => buildFilterParams(fields, values), [fields, values]);
  const hasFilters = search !== '' || Object.keys(filterParams).length > 0;

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = { page, pageSize: PAGE_SIZE, search, ...filterParams };
      if (params.includeZero === 'false') params.includeZero = false;
      const res = await inventoryApi.stock(params);
      setData(res);
    } catch (err) {
      setError(err?.message || t('Failed to load stock.'));
    } finally {
      setLoading(false);
    }
  }, [page, search, filterParams, t]);

  useEffect(() => { load(); }, [load]);

  const onSearchChange = (value) => {
    setSearchText(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => { setPage(1); setSearch(value); }, 300);
  };

  const onFilterChange = (id, value) => {
    setPage(1);
    setValues((prev) => {
      const next = { ...prev };
      if (value === undefined) delete next[id];
      else next[id] = value;
      return next;
    });
  };

  const uomCode = (uid) => units.find((u) => u.id === uid)?.code;
  const locationName = (lid) => locations.find((l) => l.id === lid)?.name;

  const totalValue = useMemo(() => {
    const cur = data.items.find((s) => s.stockValue)?.stockValue?.currency || 'USD';
    const sum = data.items.reduce((a, s) => a + Number(s.stockValue?.amount || 0), 0);
    return `${cur} ${formatAmount(sum)}`;
  }, [data.items]);

  const open = (row) => navigate(itemPath({ name: row.itemName, id: row.itemId }));

  const columns = [
    {
      key: 'item', header: t('Item'), width: 300, render: (s) => (
        <div>
          <div style={{ fontWeight: 600 }}>{s.itemName || '—'}</div>
          {s.sku && <div className="pp-cell-muted" style={{ fontSize: 'var(--text-xs)' }}>{s.sku}</div>}
        </div>
      ),
    },
    { key: 'location', header: t('Location'), width: 160, render: (s) => locationName(s.locationId) || <span className="pp-cell-muted">—</span> },
    {
      key: 'qty', header: t('On hand'), width: 120, align: 'right',
      render: (s) => <strong>{`${s.quantityOnHand}${uomCode(s.uomId) ? ' ' + uomCode(s.uomId) : ''}`}</strong>,
    },
    { key: 'avg', header: t('Avg cost'), width: 130, align: 'right', render: (s) => money(s.averageCost) },
    { key: 'value', header: t('Stock value'), width: 140, align: 'right', render: (s) => money(s.stockValue) },
    { key: 'last', header: t('Last movement'), width: 140, render: (s) => (s.lastMovementAt ? new Date(s.lastMovementAt).toLocaleDateString() : '—') },
  ];

  const totalPages = Math.max(1, Math.ceil(data.totalCount / PAGE_SIZE));

  return (
    <PageContainer size="full">
      <PageHeader
        title={t('Stock on Hand')}
        description={t('Live on-hand balances per item and location, valued at moving-average cost. Updated automatically as goods are received.')}
        icon={<Icon.Boxes size={22} />}
      />

      <div className="req-kpis">
        <KpiTile label={t('Stock lines')} value={data.totalCount} />
        <KpiTile label={t('Value (this page)')} value={totalValue} />
        <KpiTile label={t('Locations')} value={locations.length} />
      </div>

      <FilterBar
        searchPlaceholder={t('Search by item or SKU…')}
        searchValue={searchText}
        onSearchChange={onSearchChange}
        fields={fields}
        values={values}
        onChange={onFilterChange}
      />

      {error ? (
        <EmptyState
          icon={<Icon.CircleAlert size={28} />}
          title={t("Couldn't load stock")}
          description={error}
          action={<Button variant="secondary" onClick={load}>{t('Retry')}</Button>}
        />
      ) : loading ? (
        <div style={{ display: 'grid', placeItems: 'center', padding: 'var(--space-16)' }}><Spinner size={26} /></div>
      ) : (
        <>
          <DataTable
            columns={columns}
            data={data.items}
            rowKey={(s) => s.id}
            density="comfortable"
            onRowClick={open}
            emptyState={
              <EmptyState
                icon={<Icon.Boxes size={28} />}
                title={hasFilters ? t('No matching stock') : t('No stock on hand yet')}
                description={hasFilters ? t('Try adjusting your search or filters.') : t('Stock balances build up automatically as you receive goods against purchase orders.')}
              />
            }
          />
          {data.totalCount > 0 && (
            <div style={{ marginTop: 'var(--space-4)' }}>
              <Pagination page={page} pageCount={totalPages} onPageChange={setPage} totalItems={data.totalCount} pageSize={PAGE_SIZE} />
            </div>
          )}
        </>
      )}
    </PageContainer>
  );
}
